/*
 * THE ALERT-PREFERENCE LADDER AS ONE COMPONENT (decision 486's four rungs).
 *
 * Extracted from ManageNotifications.tsx when a second caller appeared: the step
 * that runs straight after somebody creates a list, which asks what the new list
 * should send. Both render the same tree over the same endpoint's payload.
 *
 * THE MECHANISM IS SHARED AND THE SURROUNDING COPY IS NOT, which is decision
 * 498's precedent from `usePushEnrolment`: the tree, its disclosure behaviour
 * and its tri-state control are RULES and must not exist twice, while the
 * sentences around them belong to the screen that is asking. The settings page
 * says "these are your notifications"; the create step says "this list is silent
 * — here is what it could send". Neither sentence is in here.
 *
 * A STRUCTURAL MOVE AND NOTHING ELSE. Every component below arrived from
 * ManageNotifications.tsx unchanged, comments included. The one thing that
 * followed it rather than staying behind is the `expanded` set: it is the
 * tree's own presentation state, it was already keyed per node, and leaving it
 * on the screen would have meant two screens maintaining a copy.
 *
 * THE THREE COMPONENTS STAY AT MODULE SCOPE (decision 492), which is a defect
 * fix that must not be undone by a move: a component declared inside a render is
 * a new component TYPE on every render, so React remounts its subtree and loses
 * focus mid-interaction. Flipping a toggle with the keyboard put focus back at
 * the top of the page.
 */

import type { TFunction } from "i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  list,
  type AlertChannel,
  type AlertPreferenceCell,
  type AlertPreferenceGroup,
  type AlertPreferenceKind,
  type AlertPreferences,
  type AlertPreferenceScope,
  type ViewAlertPreferences,
} from "../lib/api";
import styles from "./PreferenceTree.module.css";

/** One write at one rung, which is the only verb the ladder has (decision 486). */
export type SetPreference = (
  scope: AlertPreferenceScope,
  selector: string,
  channel: AlertChannel,
  enabled: boolean,
) => void;

export interface PreferenceTreeProps {
  /**
   * EITHER BODY, and the union is the whole of this component's change for
   * multi-list item 4 (decision 516). It reads `channels`, `all` and `groups`,
   * which the per-list and the merged bodies carry identically; it never reads
   * `list_id`, which is the one field they differ on.
   *
   * Widened rather than given a `list_id` on the merged side to avoid the type
   * change: a merged tree has no single list, and a field that lies to keep a
   * type quiet is worse than a type that says there are two callers.
   */
  prefs: AlertPreferences | ViewAlertPreferences;
  onSet: SetPreference;
}

export function PreferenceTree({ prefs, onSet }: PreferenceTreeProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const groups = list(prefs.groups);
  const channels = list(prefs.channels);

  const toggle = useCallback((nodeKey: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(nodeKey)) next.delete(nodeKey);
      else next.add(nodeKey);
      return next;
    });
  }, []);

  /*
   * TWO TOP-LEVEL SECTIONS BENEATH "ALL NOTIFICATIONS" (decision 528, Kevin's
   * feedback on the built ladder): "Before a Tournament", wrapping every
   * group but day-of, and "During a Tournament", the existing `dayof` group
   * unchanged. Partitioned BY ID rather than by position, so a future group
   * id lands in Before by default -- everything that is not day-of is
   * something that happens beforehand.
   */
  const during = groups.find((g) => g.id === "dayof") ?? null;
  const before = groups.filter((g) => g.id !== "dayof");

  /*
    ONE TREE, ROOTED AT "ALL NOTIFICATIONS" (Kevin's feedback). The page
    used to render the top rung as a section BESIDE the groups rather than
    above them, so the four levels decision 486 resolves through -- all,
    group, type, kind -- read as a flat list of headings with indentation
    appearing only at the bottom two. A reader could not see that the top
    row governed everything below it, which is the ladder's whole point and
    the reason a type-level row answers for kinds that do not exist yet.

    Real nested lists rather than a table, because the structure IS
    containment: a table would have to fake it with padding inside a cell
    and would fight a phone at four columns.
  */
  return (
    <ul className={styles.root}>
      <li className={styles.node}>
        <div className={styles.nodeHead}>
          <div className={styles.nodeText}>
            <h2 className={styles.h2}>{t("notifications.manage.all")}</h2>
            <p className={styles.nodeDetail}>{t("notifications.manage.allDetail")}</p>
          </div>
          <Switches
            scope="all"
            selector="all"
            cells={list(prefs.all)}
            channels={channels}
            onSet={onSet}
            t={t}
          />
        </div>

        <ul className={styles.branch}>
          {/*
            BEFORE A TOURNAMENT: presentation only, no Switches (decision 528,
            Kevin's choice over a real fifth rung -- the alternative costs a
            scope enum, a migration for stored scope='group' overrides, and a
            review of every branch on GroupOf(kind), for a heading). Build
            rule 2: absent rather than an empty section if the server ever
            sends nothing but day-of.
          */}
          {before.length === 0 ? null : (
            <li className={styles.node}>
              <div className={styles.nodeHead}>
                <div className={styles.nodeText}>
                  <h3 className={styles.sectionHeading}>
                    {t("notifications.manage.section.before")}
                  </h3>
                  <p className={styles.nodeDetail}>
                    {t("notifications.manage.section.beforeDetail")}
                  </p>
                </div>
              </div>
              <ul className={styles.branch}>
                {before.map((group) => (
                  <GroupNode
                    key={group.id}
                    group={group}
                    as="h4"
                    headingClassName={styles.h4}
                    channels={channels}
                    expanded={expanded}
                    onToggle={toggle}
                    onSet={onSet}
                    t={t}
                  />
                ))}
              </ul>
            </li>
          )}

          {/*
            DURING A TOURNAMENT is the existing `dayof` group row, unchanged
            beyond its heading's tag and class: it keeps its own Switches, so
            the two sections are deliberately not symmetrical there.
          */}
          {during ? (
            <GroupNode
              key={during.id}
              group={during}
              as="h3"
              headingClassName={styles.sectionHeading}
              channels={channels}
              expanded={expanded}
              onToggle={toggle}
              onSet={onSet}
              t={t}
            />
          ) : null}
        </ul>
      </li>
    </ul>
  );
}

/**
 * One group's row and whatever hangs below it -- a type level where the group
 * has one, or its kinds directly where it does not.
 *
 * SHARED BETWEEN THE BEFORE GROUPS AND DURING, which is why the heading tag
 * and class are props rather than fixed: the four Before groups sit one level
 * under a section and render `h4` (decision 528), while During's own row IS a
 * section heading and renders `h3` with `.sectionHeading`. Everything else --
 * the switches, the per-kind disclosure, the type level -- is identical
 * either way, which is the property worth keeping in one component rather
 * than two copies that could drift.
 *
 * AT MODULE SCOPE (decision 492): a component declared inside a render is a
 * new component type on every render, which remounts its subtree and loses
 * focus mid-interaction.
 */
function GroupNode({
  group,
  as,
  headingClassName,
  channels,
  expanded,
  onToggle,
  onSet,
  t,
}: {
  group: AlertPreferenceGroup;
  as: "h3" | "h4";
  headingClassName: string | undefined;
  channels: AlertChannel[];
  expanded: Set<string>;
  onToggle: (nodeKey: string) => void;
  onSet: SetPreference;
  t: TFunction;
}) {
  const types = list(group.types);
  // A group with no NAMED type holds its kinds directly: the rung
  // exists in the model for every group and earns a row only where
  // it has something to say (internal/alerts.PreferenceType).
  const typed = types.filter((ty) => ty.id !== "");
  const ownKinds = types.flatMap((ty) => (ty.id === "" ? list(ty.kinds) : []));
  const Heading = as;

  return (
    <li className={styles.node}>
      <div className={styles.nodeHead}>
        <div className={styles.nodeText}>
          <Heading className={headingClassName}>
            {t(`notifications.manage.group.${group.id}`)}
          </Heading>
          <p className={styles.nodeDetail}>
            {t(`notifications.manage.group.${group.id}Detail`)}
          </p>
        </div>
        <Switches
          scope="group"
          selector={group.id}
          cells={list(group.channels)}
          channels={channels}
          onSet={onSet}
          t={t}
        />
      </div>

      {typed.length === 0 ? (
        <KindList
          nodeKey={`group:${group.id}`}
          kinds={ownKinds}
          open={expanded.has(`group:${group.id}`)}
          onToggle={onToggle}
          channels={channels}
          onSet={onSet}
          t={t}
        />
      ) : (
        <ul className={styles.branch}>
          {typed.map((ty) => (
            <li key={ty.id} className={styles.node}>
              <div className={styles.nodeHead}>
                <div className={styles.nodeText}>
                  <h4 className={styles.h4}>{t(`notifications.manage.type.${ty.id}`)}</h4>
                  <p className={styles.nodeDetail}>
                    {t(`notifications.manage.type.${ty.id}Detail`)}
                  </p>
                </div>
                <Switches
                  scope="type"
                  selector={ty.id}
                  cells={list(ty.channels)}
                  channels={channels}
                  onSet={onSet}
                  t={t}
                />
              </div>

              <KindList
                nodeKey={`type:${ty.id}`}
                kinds={list(ty.kinds)}
                open={expanded.has(`type:${ty.id}`)}
                onToggle={onToggle}
                channels={channels}
                onSet={onSet}
                t={t}
              />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * One rung's controls, one per channel it offers.
 *
 * A CHANNEL A LEVEL DOES NOT OFFER GETS NO CONTROL, not a disabled one: day-of ×
 * email is a rule (decision 392) and the server sends no cell for it, so absence
 * here is the contract being read rather than a branch.
 *
 * AT MODULE SCOPE AND NOT INSIDE THE SCREEN, which it used to be and which was a
 * defect rather than a style preference: a component declared inside a render is
 * a NEW COMPONENT TYPE on every render, so React unmounts and remounts the whole
 * subtree instead of updating it. Every checkbox was replaced on every state
 * change, which loses focus mid-interaction -- flip a toggle with the keyboard
 * and focus lands back at the top of the page. Found while moving the disclosure
 * per node, where the same shape ALSO made a freshly-toggled `aria-expanded`
 * unobservable because the button element itself had been swapped.
 */
function Switches({
  scope,
  selector,
  cells,
  channels,
  onSet,
  t,
}: {
  scope: AlertPreferenceScope;
  selector: string;
  cells: AlertPreferenceCell[];
  channels: AlertChannel[];
  onSet: SetPreference;
  t: TFunction;
}) {
  return (
    <div className={styles.switches}>
      {channels.map((channel) => {
        const cell = cells.find((c) => c.channel === channel);
        if (!cell) return null;
        return (
          <Switch
            key={channel}
            cell={cell}
            label={t(`notifications.manage.channel.${channel}`)}
            mixedLabel={t("notifications.manage.mixed")}
            onChange={(enabled) => onSet(scope, selector, channel, enabled)}
          />
        );
      })}
    </div>
  );
}

/**
 * The per-kind fine-tuning beneath one node, behind its own disclosure (§5).
 *
 * A REAL <button> WITH aria-expanded rather than a <details>, matching the
 * disclosure pattern direction §7 names for correct ARIA -- and the content is
 * real DOM text once open, so a screen reader meets it in reading order.
 *
 * ONE PER NODE THAT DIRECTLY HOLDS KINDS, which is Kevin's feedback and was a
 * real defect rather than a layout complaint. "During a tournament" had ONE
 * control at the bottom of four types and fourteen kinds, so a reader who
 * pressed it got a flat list with nothing saying which kind belonged to which
 * type -- the grouping decision 486 exists for, undone at exactly the moment it
 * mattered. A group with no type level keeps its own, because there the group IS
 * the node that holds the kinds.
 *
 * THE KEY IS SCOPED (`type:dayof_progress`) rather than bare, so a group id and a
 * type id cannot collide in the expanded set: they are different vocabularies and
 * nothing stops one borrowing the other's string.
 *
 * A node holding ONE kind gets no disclosure, because the node's own row already
 * governs that kind and nothing else -- the control would reveal a duplicate of
 * the row above it. Build rule 3's plural chrome, applied to a disclosure.
 */
function KindList({
  nodeKey,
  kinds,
  open,
  onToggle,
  channels,
  onSet,
  t,
}: {
  nodeKey: string;
  kinds: AlertPreferenceKind[];
  open: boolean;
  onToggle: (nodeKey: string) => void;
  channels: AlertChannel[];
  onSet: SetPreference;
  t: TFunction;
}) {
  if (kinds.length <= 1) return null;

  return (
    <>
      <button
        type="button"
        className={styles.disclosure}
        aria-expanded={open}
        onClick={() => onToggle(nodeKey)}
      >
        {t("notifications.manage.perKind")}
      </button>

      {open ? (
        <ul className={styles.branch}>
          {kinds.map((kind) => (
            <li key={kind.kind} className={styles.node}>
              <div className={styles.nodeHead}>
                <span className={styles.kindName}>
                  {t(`notifications.manage.kindLabel.${kind.kind}`)}
                </span>
                <Switches
                  scope="kind"
                  selector={kind.kind}
                  cells={list(kind.channels)}
                  channels={channels}
                  onSet={onSet}
                  t={t}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

/**
 * One toggle, three states.
 *
 * THE PARENT RUNGS ARE TRI-STATE AND A BOOLEAN CANNOT SAY SO (decision 486).
 * A group whose kinds disagree is neither on nor off, and rounding either way
 * misreports: `false` on a group with one kind enabled claims nothing arrives,
 * and `true` claims everything does. `indeterminate` is the platform's own
 * answer and it carries to assistive technology as `aria-checked="mixed"`
 * without being asked.
 *
 * IT IS SET THROUGH A REF because `indeterminate` is a DOM property and not an
 * HTML attribute -- React has no prop for it, which is the one thing about this
 * control that is not obvious from the JSX.
 *
 * The WORD is there too, because direction §7 bars a state that reaches the
 * reader through a visual channel alone: "Some on" renders with the control
 * rather than instead of it.
 *
 * IT IS ON ITS OWN LINE, which is Kevin's second piece of feedback and a layout
 * fix rather than a copy one. Inline, "On this device" and "Some on" sat against
 * each other with nothing between them and read as one run of text that looked
 * like a malformed label. Beneath, indented to the label's own inline start, it
 * reads as what it is: a state belonging to that control and to nothing beside
 * it. The checkbox and its label stay on ONE line inside `.switchLine`, which is
 * what keeps the control from becoming three stacked things.
 */
function Switch({
  cell,
  label,
  mixedLabel,
  onChange,
}: {
  cell: AlertPreferenceCell;
  label: string;
  mixedLabel: string;
  onChange: (enabled: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const mixed = cell.state === "mixed";

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = mixed;
  }, [mixed]);

  return (
    <label className={styles.switch}>
      <span className={styles.switchLine}>
        <input
          ref={ref}
          type="checkbox"
          checked={cell.state === "on"}
          // A MIXED PARENT TURNS EVERYTHING ON, which is the less destructive of
          // the two readings: the alternative silences kinds the reader had
          // deliberately enabled, and they would have no way to tell which.
          onChange={(e) => onChange(mixed ? true : e.target.checked)}
        />
        <span>{label}</span>
      </span>
      {mixed ? <span className={styles.mixed}>{mixedLabel}</span> : null}
    </label>
  );
}
